import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';

import { CompaniesService } from '@services/companies.service';
import { UserDeletionBlockersResultModel, UserModel } from '@data-layer/models/user';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { VerifyPhoneNumberComponent } from '@shared/verify-phone-number/components';
import { INPUT_TYPES_MAP } from '@constants/input-types-map';
import { FormHelper } from '@helpers/form.helper';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { enumRespErrors } from '@data-layer/enums/common';
import { LoginAttemptModel } from '@data-layer/models/common';
import { VerificationCodeHelper } from '@helpers/verification-code.helper';
import { WidgetsService } from '@services/widgets.service';
import { LA_TIMEZONE_NAME } from '@constants/constants';
import { omit } from 'lodash-es';

@Component({
  selector: 'user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent implements OnInit, OnChanges, OnDestroy {
  //TODO: DEPRECATED COMPONENT - REMOVE. New account settings on the prod.

  @Input() public initialSection: string;
  @Output() public navigateToElement: EventEmitter<string> = new EventEmitter<string>();
  @Output() public afterSave: EventEmitter<any> = new EventEmitter<any>();
  @Output() public afterDelete: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(VerifyPhoneNumberComponent, { static: false }) public verifyModal: VerifyPhoneNumberComponent;
  @ViewChild('hiddeninput', { static: false }) public hiddeninput: ElementRef;

  public formGroup: UntypedFormGroup;
  public user: UserModel;
  public errors: { [name: string]: IErrorForm };
  public isChangedPhoneNumber = false;
  public CUSTOM_MASK = CUSTOM_MASK;
  public integerInputMap = INPUT_TYPES_MAP.INTEGER;
  public loginAttempts: LoginAttemptModel[] = [];
  public deletionBlockers: UserDeletionBlockersResultModel;
  public openCancelAccountModal: boolean;
  public deletionReasonControl: UntypedFormControl = new UntypedFormControl();

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject();
  private formattedPhoneNumber: string;

  constructor(
    private fb: UntypedFormBuilder,
    private fh: FormHelper,
    private companiesService: CompaniesService,
    private widgetsService: WidgetsService,
  ) {}

  public get isVerificationCodeExpired(): boolean {
    if (this.formGroup.get('phoneNumber').invalid || !this.formGroup.get('phoneNumber').value) {
      return true;
    }
    return VerificationCodeHelper.isVerificationCodeExpired(this.formattedPhoneNumber);
  }

  public ngOnInit(): void {
    this.formGroup = this.fb.group(this.fh.createUserFormGroup());
    this.user = this.companiesService.getUser();
    this.companiesService
      .getUserLoginAttempts(0, 5)
      .pipe(takeUntil(this.isDestroy))
      .subscribe((response) => {
        this.loginAttempts = response.items;
      });
    this.formGroup.patchValue({ ...this.user, timeZone: this.user.timeZone || LA_TIMEZONE_NAME });
    this.createFormsErrors();

    this.formGroup
      .get(['phoneNumber'])
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe((data) => {
        if (data) {
          this.formattedPhoneNumber = data;
          this.isChangedPhoneNumber =
            this.user.phoneNumberConfirmed && data !== undefined && this.formattedPhoneNumber !== this.user.phoneNumber;
        } else {
          this.formattedPhoneNumber = undefined;
        }
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialSection && this.initialSection) {
      this.navigateToSection(this.initialSection);
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public getDeletionBlockers() {
    this.companiesService
      .getDeletionBlockers()
      .pipe(takeUntil(this.isDestroy))
      .subscribe((deletionBlockers) => {
        this.deletionBlockers = deletionBlockers;
        this.openCancelAccountModal = true;
      });
  }

  public cancelAccount() {
    this.companiesService
      .deleteUser(this.deletionReasonControl.value)
      .pipe(takeUntil(this.isDestroy))
      .subscribe(() => {
        this.afterDelete.emit();
      });
  }

  public contactSupport() {
    this.widgetsService.openWidget(['cancel_my_account']);
  }

  public openVerifyModal() {
    if (!this.formGroup.get('phoneNumber').invalid && this.formattedPhoneNumber) {
      this.hiddeninput.nativeElement.focus();
      this.updatePhoneNumber();
      this.verify();
    }
  }

  public save() {
    if (this.formGroup.invalid) {
      this.scrollToFirstError();
      return;
    }

    const value = omit(this.formGroup.value, ['phoneNumber', 'timeZone']);
    const phoneNumber: string = this.formGroup.get('phoneNumber').value;
    const timezoneValue: any = this.formGroup.get('timeZone').value;

    this.companiesService
      .updateUser({ ...this.companiesService.getUser(), ...value, phoneNumber, timeZone: timezoneValue.nameValue })
      .pipe(
        switchMap(() => this.companiesService.getUserData(true)),
        takeUntil(this.isDestroy),
      )
      .subscribe(() => {
        this.afterSave.emit();
      });
  }

  public loginError(error: enumRespErrors): string {
    let message: string;
    switch (error) {
      case enumRespErrors.AccountTemporarilyLocked:
        message = 'FORM_ERROR.LOCKED_ACCOUNT_SHORT';
        break;
      case enumRespErrors.InvalidUsernameOrPassword:
        message = 'FORM_ERROR.INVALID_CREDENTIALS';
        break;
      case enumRespErrors.EmailNotConfirmed:
        message = 'FORM_ERROR.EMAIL_IS_NOT_CONFIRM';
        break;
    }
    return message;
  }

  private verify() {
    this.verifyModal.openModal(this.formattedPhoneNumber, this.user);

    if (this.isVerificationCodeExpired) {
      this.companiesService
        .phoneConfirmation(this.formattedPhoneNumber)
        .pipe(takeUntil(this.isDestroy))
        .subscribe(() => {
          VerificationCodeHelper.setVerificationCode(this.formattedPhoneNumber);
        });
    }
  }

  private updatePhoneNumber() {
    if (this.formattedPhoneNumber === this.companiesService.getUser().phoneNumber) {
      return;
    }
    this.companiesService
      .updateUser({ ...this.companiesService.getUser(), phoneNumber: this.formattedPhoneNumber })
      .pipe(
        switchMap(() => this.companiesService.getUserData()),
        takeUntil(this.isDestroy),
      )
      .subscribe();
  }

  private scrollToFirstError() {
    for (const controlName in this.formGroup.value) {
      if (this.formGroup.get(controlName) && this.formGroup.get(controlName).invalid) {
        this.navigateToSection('control-' + controlName);
        return;
      }
    }
  }

  private navigateToSection(fragmentId: string) {
    this.navigateToElement.emit(fragmentId);
  }

  private createFormsErrors(): void {
    this.errors = {
      firstName: {
        required: 'FORM_ERROR.REQUIRED',
      },
      lastName: {
        required: 'FORM_ERROR.REQUIRED',
      },
      email: {
        required: 'FORM_ERROR.REQUIRED',
      },
      phoneNumber: {
        maxlength: 'FORM_ERROR.MAX_LENGTH',
        validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER',
      },
    };
  }
}
